import {
  Card,
  Grid,
  Group,
  Indicator,
  SimpleGrid,
  Stack,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useRouter } from "next/navigation";
import { type ReactNode, Suspense } from "react";
import { FaTrophy } from "react-icons/fa";

import {
  Avatar,
  DisLikeIcon,
  EditIcon,
  H2,
  LikeIcon,
  LoadingOverlay,
  Paper,
  SafetyCertificateIcon,
  Text,
} from "@/components/ui/elements";
import classes from "./_css/UserInfo.module.css";
import { gradientTextStyle } from "./_function/workReward";
import { ApplicationRestrictionCard } from "./ApplicationRestrictionCard";
import { ApplicationStatusCard } from "./ApplicationStatusCard";
import type { CancellationStats } from "./CancellationStatsModal";
import { CancellationStatsModal } from "./CancellationStatsModal";
import { FollowerOfficeCountSection } from "./FollowerOfficeCountSection";
import { KyashBalanceCard } from "./KyashBalanceCard";
import { PointCard } from "./PointCard";
import { PointHistoryModal } from "./PointHistoryModal";
import { ReviewListModal } from "./ReviewListModal";
import { SuspendedAccountCard } from "./SuspendedAccountCard";
import { SuspendedProfileBadge } from "./SuspendedProfileBadge";
import type { TrustScore } from "./TrustScoreCard";
import { TrustScoreCard } from "./TrustScoreCard";
import { WorkerPointModal } from "./WorkerPointModal";

type Props = {
  profile: {
    imageUrl: string | null;
    lastName: string;
    firstName: string;
    authenticated: boolean;
  } | null;
  usageStatus: {
    workedCount: number;
    goodReviewCount: number;
    badReviewCount: number;
    lateCancellationCountThisMonth: number;
    monthlyTotalWorkingHour: number;
    employedCount: number;
    trustScore: TrustScore | null;
    cancellationStatsLastThreeMonths: CancellationStats | null;
    cancellationStatsAllPeriod: CancellationStats | null;
  } | null;
  points: number;
  viewer: {
    status: string | null;
    isSuspended: boolean | null;
    suspensionStartDateAt: string | null;
    suspensionEndDateAt: string | null;
    applicationStatus: { currentCount: number; limit: number } | null;
    isFirstWorkMonth: boolean;
  } | null;
  unreviewedWarning?: ReactNode;
};

export function UserInfo({
  profile,
  usageStatus,
  points,
  viewer,
  unreviewedWarning,
}: Props) {
  const router = useRouter();
  const [pointModalOpened, { close: closePointModal, open: openPointModal }] =
    useDisclosure();
  const [
    cancellationStatsModalOpened,
    { close: closeCancellationStatsModal, open: openCancellationStatsModal },
  ] = useDisclosure();
  const [
    pointHistoryModalOpened,
    { close: closePointHistoryModal, open: openPointHistoryModal },
  ] = useDisclosure();
  const [reviewListOpened, { close: closeReviewList, open: openReviewList }] =
    useDisclosure();

  // 計算値
  const actualWorkHours = usageStatus?.monthlyTotalWorkingHour || 0;
  const lateCancellationCount =
    usageStatus?.lateCancellationCountThisMonth ?? 0;

  // ステータス判定（モバイル UserInfoCardsSection と同条件）
  const isForceWithdown = viewer?.status === "forced_withdrawn";
  const isSuspended = viewer?.isSuspended === true;
  const showApplicationRestriction =
    !isForceWithdown && (isSuspended || points <= 0);

  // プロフィール未設定時
  if (!profile) {
    return (
      <Paper>
        <Grid pb="lg" align="flex-end" gutter="sm">
          <Grid.Col span="auto">
            <Avatar color="gray" variant="outline" />
          </Grid.Col>
          <Grid.Col span="auto">
            <H2>未設定</H2>
          </Grid.Col>
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper>
      <Stack gap="sm">
        {isForceWithdown ? <SuspendedAccountCard /> : null}

        {/* プロフィール部分 */}
        <Grid align="center" gutter="xs">
          <Grid.Col span={3}>
            <UnstyledButton
              disabled={isForceWithdown}
              onClick={
                isForceWithdown ? undefined : () => router.push("/mypage/edit")
              }
            >
              <Indicator
                disabled={isForceWithdown}
                label={<EditIcon size={10} />}
                size={20}
                position="bottom-end"
                offset={6}
                zIndex={10}
              >
                <Avatar
                  src={profile.imageUrl}
                  color="gray"
                  variant="outline"
                  size="lg"
                />
              </Indicator>
            </UnstyledButton>
          </Grid.Col>

          <Grid.Col span="auto">
            {/* 利用停止のみ氏名上バッジ。応募制限は統計カード群先頭のカードで表示（モバイル準拠） */}
            {isForceWithdown ? <SuspendedProfileBadge /> : null}
            <Text fz={20} fw="bold">
              {profile.lastName}　{profile.firstName}
            </Text>
            {profile.authenticated && !isForceWithdown ? (
              <Group gap="xs">
                <SafetyCertificateIcon
                  size={12}
                  color="var(--mantine-color-teal-6)"
                />
                <Text c="teal" fw="bold" fz="xs">
                  本人確認済
                </Text>
              </Group>
            ) : null}
          </Grid.Col>
        </Grid>
      </Stack>

      {/* 勤務実績セクション（1カラムずつ表示） */}
      <Stack pt="xs" gap="sm">
        {showApplicationRestriction ? (
          <ApplicationRestrictionCard
            suspensionStartDateAt={viewer?.suspensionStartDateAt ?? null}
            suspensionEndDateAt={viewer?.suspensionEndDateAt ?? null}
            bodyKind={points <= 0 ? "pointsAtOrBelowZero" : "policySuspension"}
          />
        ) : null}

        {/* 給与残高（利用停止でも獲得報酬への導線として出す） */}
        <KyashBalanceCard />

        {/* 今月の勤務実績（2026-11 からの全特典廃止により、勤務特典の額・詳細・特典基準の進捗は出さない） */}
        <Card
          withBorder
          p="sm"
          radius="lg"
          style={{
            background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)",
            border: "1px solid #d1fae5",
          }}
        >
          <Text fw="bold" fz="sm" c="dark.7" mb="xs">
            今月の勤務実績
          </Text>
          <Group
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            gap="sm"
          >
            <Group gap={4} align="baseline">
              <Text
                fz={{ base: 24, sm: 28 }}
                fw="bold"
                style={gradientTextStyle.teal}
              >
                {actualWorkHours.toFixed(1)}
              </Text>
              <Text c="dark.7" fz="sm" fw="500">
                時間
              </Text>
            </Group>
            {lateCancellationCount > 0 && (
              <Text c="orange.7" fz="xs" fw="500">
                直前キャンセル: {lateCancellationCount}件
              </Text>
            )}
          </Group>
        </Card>

        {/* 累計勤務実績 */}
        <Card
          withBorder
          p="sm"
          radius="lg"
          style={{
            background: "linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)",
            border: "1px solid #bfdbfe",
          }}
        >
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <Text fw="bold" fz="sm" c="dark.7">
              累計勤務実績
            </Text>
            <Group gap={4} align="baseline">
              <Text
                fz={{ base: 24, sm: 28 }}
                fw="bold"
                style={gradientTextStyle.blue}
              >
                {usageStatus?.workedCount || 0}
              </Text>
              <Text c="dark.7" fz="sm" fw="500">
                回勤務
              </Text>
            </Group>
            {viewer?.isFirstWorkMonth && (
              <Group gap={4} align="center">
                <FaTrophy color="#f59e0b" size={14} />
                <Text fz="xs" fw="bold" c="orange.6">
                  初回勤務達成済み
                </Text>
              </Group>
            )}
          </Group>
        </Card>

        {/* 信頼スコア ※勤務がある場合のみ表示。算出できなかったときは表示しない */}
        {(usageStatus?.employedCount ?? 0) > 0 && usageStatus?.trustScore ? (
          <TrustScoreCard
            trustScore={usageStatus.trustScore}
            onOpenCancellationStats={openCancellationStatsModal}
          />
        ) : null}

        {/* 応募状況 & 持ち点 */}
        <SimpleGrid cols={2} spacing="sm">
          <ApplicationStatusCard
            isForceWithdown={isForceWithdown}
            applicationStatus={viewer?.applicationStatus ?? null}
          />
          <PointCard
            points={points}
            isForceWithdown={isForceWithdown}
            onOpenDetail={openPointModal}
            onOpenHistory={openPointHistoryModal}
          />
        </SimpleGrid>

        {unreviewedWarning}

        {/* 施設目線の情報（フォロー施設数・評価） */}
        <Card className={classes.reviewCard} p="lg" radius="lg">
          <FollowerOfficeCountSection />
          <Text className={classes.reviewTitle}>施設からの評価</Text>
          <Suspense fallback={<LoadingOverlay />}>
            <ReviewListModal
              type="good"
              opened={reviewListOpened}
              close={closeReviewList}
            />
          </Suspense>
          <Group justify="center" gap={48}>
            <UnstyledButton
              className={classes.reviewDetailButton}
              onClick={openReviewList}
            >
              <Group gap="xs" wrap="nowrap">
                <LikeIcon
                  className={classes.goodReviewIcon}
                  size={26}
                  aria-hidden="true"
                />
                <Text className={classes.reviewCount}>
                  {usageStatus?.goodReviewCount || 0}
                </Text>
                <Text className={classes.reviewDetail}>詳細</Text>
              </Group>
            </UnstyledButton>
            <Group gap="xs" wrap="nowrap">
              <DisLikeIcon
                className={classes.badReviewIcon}
                size={26}
                aria-hidden="true"
              />
              <Text className={classes.reviewCount}>
                {usageStatus?.badReviewCount || 0}
              </Text>
            </Group>
          </Group>
        </Card>
      </Stack>

      {/* モーダル */}
      <WorkerPointModal opened={pointModalOpened} close={closePointModal} />
      {usageStatus ? (
        <CancellationStatsModal
          opened={cancellationStatsModalOpened}
          close={closeCancellationStatsModal}
          lastThreeMonths={usageStatus.cancellationStatsLastThreeMonths}
          allPeriod={usageStatus.cancellationStatsAllPeriod}
        />
      ) : null}
      <PointHistoryModal
        opened={pointHistoryModalOpened}
        close={closePointHistoryModal}
        points={points}
      />
    </Paper>
  );
}
